import React, { useState } from "react";
import { BACKEND_URL } from "../lib/api";

/**
 * UserAvatar — single rendering contract for every walker avatar surface.
 *
 * Resolution order (per feature spec):
 *   1. `avatar_url` the walker uploaded on Profile → cache-busted with
 *      `?v=avatar_updated_at` so a swapped photo appears instantly.
 *   2. `picture` from their Google OAuth profile.
 *   3. Initials disc on brand-green.
 *
 * The brand-green disc is a bare SVG — no image request, no 404 flicker,
 * safe to drop into tight review cards and photo credits (32-40px) as
 * well as the 64px avatar on Profile.
 */
const BRAND_GREEN = "#3F5D44";
const BRAND_SAND = "#F6EFE0";

function initialsOf(nameOrEmail) {
  const src = (nameOrEmail || "").trim();
  if (!src) return "·";
  const atIdx = src.indexOf("@");
  const base = (atIdx > 0 ? src.slice(0, atIdx) : src).replace(/[^\p{L}\p{N}\s]/gu, " ").trim();
  if (!base) return "·";
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

function resolveSrc(user) {
  if (!user) return "";
  const uploaded = user.avatar_url || user.author_avatar_url || user.user_avatar_url;
  if (uploaded) {
    const stamp = user.avatar_updated_at || user.author_avatar_updated_at || user.user_avatar_updated_at || "";
    const abs = uploaded.startsWith("http") ? uploaded : `${BACKEND_URL}${uploaded}`;
    return stamp ? `${abs}?v=${encodeURIComponent(stamp)}` : abs;
  }
  const google = user.picture || user.author_picture || user.user_picture;
  return google || "";
}

export default function UserAvatar({ user, size = 32, className = "", testId }) {
  const src = resolveSrc(user);
  const [failed, setFailed] = useState(false);
  const px = { width: size, height: size };
  const label =
    user?.name || user?.user_name || user?.author_dog_name || user?.email || "";
  const initials = initialsOf(label);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        onError={() => setFailed(true)}
        style={px}
        data-testid={testId || "user-avatar-img"}
        className={`rounded-full object-cover border border-brand-border bg-brand-sand ${className}`}
      />
    );
  }

  const fontSize = Math.round(size * 0.42);
  return (
    <div
      style={{ ...px, background: BRAND_GREEN, color: BRAND_SAND, fontSize }}
      data-testid={testId || "user-avatar-initials"}
      aria-label={label || "Walker"}
      className={`rounded-full flex items-center justify-center font-semibold select-none border border-brand-border ${className}`}
    >
      {initials}
    </div>
  );
}
